import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { expenseUserPageShellSx } from "../ExpensesUser/expenseUserLayout";
import {
  pageContentCardSx,
  pageHeroShellSx,
  pageFieldSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";
import {
  addTpvLinea,
  closeTpvCaja,
  cobroTpvTicket,
  createTpvTicket,
  deleteTpvLinea,
  deleteTpvTicket,
  getTpvCaja,
  getTpvCajaPrint,
  getTpvTicket,
  getTpvTicketPrint,
  patchTpvLinea,
  reopenTpvCaja,
  reopenTpvTicket,
  searchTpvProductos,
} from "../../api/Tpv/tpvApi";
import {
  calcCambio,
  formatEur,
  formatTpvDate,
  toMoney,
} from "../../utils/tpvCalc";
import {
  cajaPrintHtml,
  openTpvPrintWindow,
  ticketPrintHtml,
} from "../../utils/tpvPrint";

function statusChipSx(theme, closed) {
  if (closed) {
    return {
      fontWeight: 800,
      bgcolor: alpha(theme.palette.info.main, 0.16),
      color: theme.palette.info.dark,
      border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
    };
  }
  return {
    fontWeight: 800,
    bgcolor: alpha(theme.palette.success.main, 0.18),
    color: theme.palette.success.dark,
    border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
  };
}

function paymentChipSx(theme, kind) {
  if (kind === "card") {
    return {
      fontWeight: 800,
      bgcolor: "#F57C00",
      color: "#fff",
      border: "1px solid #EF6C00",
    };
  }
  if (kind === "mix") {
    return {
      fontWeight: 800,
      bgcolor: "#6A1B9A",
      color: "#fff",
      border: "1px solid #4A148C",
    };
  }
  return {
    fontWeight: 800,
    bgcolor: "#2E7D32",
    color: "#fff",
    border: "1px solid #1B5E20",
  };
}

function ticketPaymentKind(row) {
  const cash = Number(row?.efectivo || 0) > 0;
  const card = Number(row?.tarjeta || 0) > 0;
  if (cash && card) return "mix";
  if (card) return "card";
  if (cash) return "cash";
  return null;
}

function currentIsAdmin() {
  try {
    const payload = jwtDecode(localStorage.getItem("token") || "");
    return !!payload.isAdmin;
  } catch {
    return false;
  }
}

function KpiRow({ label, value, accent }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.85,
        px: 1.25,
        borderRadius: 2,
        bgcolor: accent
          ? alpha(accent, 0.08)
          : alpha(theme.palette.background.default, 0.65),
      }}
    >
      <Typography
        sx={{
          ...pageSectionTitleSx,
          mb: 0,
          textTransform: "none",
          letterSpacing: 0,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function TpvCajaPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { cajaId, ticketId } = useParams();
  const { contentMargin } = useContext(SidebarContext);
  const eanRef = useRef(null);
  const buscaRef = useRef(null);
  const focusedTicketRef = useRef(null);
  const searchTimer = useRef(null);
  const isAdmin = useMemo(() => currentIsAdmin(), []);

  const [caja, setCaja] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ean, setEan] = useState("");
  const [busca, setBusca] = useState("");
  const [hits, setHits] = useState([]);
  const [cobroOpen, setCobroOpen] = useState(false);
  const [reopenTicketOpen, setReopenTicketOpen] = useState(false);
  const [closeCajaOpen, setCloseCajaOpen] = useState(false);
  const [reopenCajaOpen, setReopenCajaOpen] = useState(false);
  const [deleteTicketTarget, setDeleteTicketTarget] = useState(null);
  const [efectivoOn, setEfectivoOn] = useState(false);
  const [tarjetaOn, setTarjetaOn] = useState(false);
  const [entregaE, setEntregaE] = useState("0");
  const [entregaC, setEntregaC] = useState("0");
  const [aEntregar, setAEntregar] = useState("0");
  const [busy, setBusy] = useState(false);

  const loadCaja = useCallback(async () => {
    const data = await getTpvCaja(cajaId);
    setCaja(data);
    setTickets(data.tickets || []);
    setAEntregar(String(data.aEntregar ?? 0));
    return data;
  }, [cajaId]);

  const loadTicket = useCallback(async (id) => {
    if (!id) {
      setTicket(null);
      return;
    }
    const data = await getTpvTicket(id);
    setTicket(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await loadCaja();
      if (ticketId) await loadTicket(ticketId);
      else setTicket(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("Tpv.loadError"));
    } finally {
      setLoading(false);
    }
  }, [loadCaja, loadTicket, t, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  // Solo al entrar en un ticket abierto; no al editar líneas (Tab/Enter).
  useEffect(() => {
    if (!ticketId) {
      focusedTicketRef.current = null;
      return;
    }
    if (!ticket?.id || ticket.cerrado) return;
    if (Number(ticket.id) !== Number(ticketId)) return;
    if (focusedTicketRef.current === ticket.id) return;
    focusedTicketRef.current = ticket.id;
    requestAnimationFrame(() => buscaRef.current?.focus());
  }, [ticketId, ticket]);

  const locked = !!(caja?.cerrado || ticket?.cerrado);
  const total = toMoney(ticket?.total);
  const cambio = calcCambio(
    total,
    efectivoOn ? Number(String(entregaE).replace(",", ".")) : 0,
    tarjetaOn ? Number(String(entregaC).replace(",", ".")) : 0,
  );
  const totalCaja = toMoney(
    Number(caja?.importeIni || 0) +
      Number(caja?.ingMetalico || 0) +
      Number(caja?.ingTarjeta || 0),
  );
  const enCaja = toMoney(
    Number(caja?.importeIni || 0) +
      Number(caja?.ingMetalico || 0) -
      Number(String(aEntregar).replace(",", ".") || 0),
  );
  const selectedTicketId = ticket?.id || Number(ticketId) || null;

  const goTicket = (id) => {
    if (id) navigate(`/tpv/${cajaId}/ticket/${id}`);
    else navigate(`/tpv/${cajaId}`);
  };

  const handleNewTicket = async () => {
    setBusy(true);
    setError("");
    try {
      const created = await createTpvTicket(cajaId);
      goTicket(created.id);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Tpv.ticketError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const addByEan = async (code, extra = {}) => {
    const value = String(code || ean).trim();
    if (!ticket?.id) {
      setError(t("Tpv.needTicket"));
      return;
    }
    if (!value) return;
    setBusy(true);
    setError("");
    try {
      const data = await addTpvLinea(ticket.id, value, extra);
      setEan("");
      setBusca("");
      setHits([]);
      await loadTicket(ticket.id);
      await loadCaja();
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("Tpv.eanError"));
    } finally {
      setBusy(false);
      buscaRef.current?.focus();
    }
  };

  const addFreeText = async (text) => {
    const concepto = String(text || busca).trim();
    if (!concepto) return;
    await addByEan("1", { concepto, pvp: 0 });
  };

  const onSearch = (value) => {
    setBusca(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (String(value).trim().length < 2) {
      setHits([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        setHits(await searchTpvProductos(value));
      } catch {
        setHits([]);
      }
    }, 250);
  };

  const onSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = String(busca).trim();
    if (!q) return;
    if (hits.length === 1) {
      addByEan(hits[0].ean);
      return;
    }
    const exact = hits.find(
      (h) =>
        String(h.ean).toLowerCase() === q.toLowerCase() ||
        String(h.concepto || "").toLowerCase() === q.toLowerCase(),
    );
    if (exact) {
      addByEan(exact.ean);
      return;
    }
    addFreeText(q);
  };

  const updateLine = async (line, patch) => {
    try {
      await patchTpvLinea(line.id, patch);
      await loadTicket(ticket.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  /** Enter avanza a la siguiente casilla de la tabla; al final → Buscar prenda. */
  const onLineFieldKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const table = e.currentTarget.closest("table");
    const inputs = Array.from(
      table?.querySelectorAll("input:not([disabled])") || [],
    );
    const idx = inputs.indexOf(e.target);
    if (idx >= 0 && idx < inputs.length - 1) {
      const next = inputs[idx + 1];
      next.focus();
      next.select?.();
      return;
    }
    buscaRef.current?.focus();
  };

  const removeLine = async (line) => {
    if (!window.confirm(t("Tpv.deleteLine"))) return;
    try {
      await deleteTpvLinea(line.id);
      await loadTicket(ticket.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const openCobro = () => {
    if (!ticket?.lineas?.length) {
      setError(t("Tpv.noLines"));
      return;
    }
    if ((ticket.lineas || []).some((l) => Number(l.pvp) === 0)) {
      setError(t("Tpv.zeroPvp"));
      return;
    }
    setEfectivoOn(false);
    setTarjetaOn(false);
    setEntregaE("0");
    setEntregaC("0");
    setCobroOpen(true);
  };

  const printTicket = async (id = ticket?.id) => {
    if (!id) return;
    try {
      const data = await getTpvTicketPrint(id);
      const html = await ticketPrintHtml({
        ...data,
        ticket: {
          ...data.ticket,
          openedAtLabel: formatTpvDate(data.ticket.openedAt),
        },
      });
      openTpvPrintWindow(html);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const printCaja = async () => {
    try {
      const data = await getTpvCajaPrint(cajaId);
      const html = await cajaPrintHtml({
        ...data,
        caja: {
          ...data.caja,
          openedAtLabel: formatTpvDate(data.caja.openedAt),
          closedAtLabel: formatTpvDate(data.caja.closedAt),
        },
      });
      openTpvPrintWindow(html);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleCobro = async () => {
    if (!efectivoOn && !tarjetaOn) {
      setError(t("Tpv.needPayment"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const paidId = ticket.id;
      await cobroTpvTicket(paidId, {
        efectivoOn,
        tarjetaOn,
        entregaEfectivo: Number(String(entregaE).replace(",", ".")),
        entregaTarjeta: Number(String(entregaC).replace(",", ".")),
      });
      setCobroOpen(false);
      await printTicket(paidId);
      await loadCaja();
      goTicket(null);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Tpv.chargeError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteTicket = async () => {
    const row = deleteTicketTarget;
    if (!row?.id) return;
    setBusy(true);
    setError("");
    try {
      await deleteTpvTicket(row.id);
      const wasCurrent = Number(row.id) === Number(ticket?.id || ticketId);
      setDeleteTicketTarget(null);
      await loadCaja();
      if (wasCurrent) goTicket(null);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Tpv.deleteTicketError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCloseCaja = () => {
    if (Number(caja?.openTickets) > 0) {
      setError(t("Tpv.openTicketsBlock"));
      return;
    }
    setCloseCajaOpen(true);
  };

  const confirmCloseCaja = async () => {
    const n = Number(String(aEntregar).replace(",", "."));
    if (!Number.isFinite(n) || n < 0) {
      setError(t("Tpv.aEntregarInvalid"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await closeTpvCaja(cajaId, {
        aEntregar: n,
        confirmEmptyHandover: n === 0,
      });
      setCloseCajaOpen(false);
      await printCaja();
      navigate("/tpv");
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("Tpv.closeError"));
    } finally {
      setBusy(false);
    }
  };

  const confirmReopenCaja = async () => {
    setBusy(true);
    setError("");
    try {
      await reopenTpvCaja(cajaId);
      setReopenCajaOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmReopenTicket = async () => {
    if (!ticket?.id) return;
    setBusy(true);
    setError("");
    try {
      await reopenTpvTicket(ticket.id);
      setReopenTicketOpen(false);
      await loadCaja();
      await loadTicket(ticket.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: "100%" })}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: "100%" })}>
      <Paper sx={pageHeroShellSx(theme)} elevation={0}>
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton onClick={() => navigate("/tpv")} size="small">
              <ArrowBackOutlinedIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {t("Tpv.cajaN", { id: caja?.id })}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatTpvDate(caja?.openedAt)} · {caja?.openedByName || ""}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={caja?.cerrado ? t("Tpv.closed") : t("Tpv.open")}
              sx={statusChipSx(theme, !!caja?.cerrado)}
            />
            {!caja?.cerrado ? (
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                onClick={handleNewTicket}
                disabled={busy}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  display: { xs: "none", sm: "inline-flex" },
                }}
              >
                {t("Tpv.newTicket")}
              </Button>
            ) : null}
            {!caja?.cerrado ? (
              <IconButton
                color="primary"
                onClick={handleNewTicket}
                disabled={busy}
                sx={{ display: { xs: "inline-flex", sm: "none" } }}
              >
                <AddOutlinedIcon />
              </IconButton>
            ) : null}
          </Stack>
        </Box>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(240px, 280px) minmax(0, 1fr)",
            lg: "minmax(260px, 300px) minmax(0, 1fr) minmax(260px, 320px)",
          },
        }}
      >
        {/* Lista de tickets — izquierda */}
        <Paper
          elevation={0}
          sx={{
            ...pageContentCardSx(theme),
            p: 0,
            overflow: "hidden",
            position: { lg: "sticky" },
            top: { lg: 12 },
            maxHeight: { lg: "calc(100dvh - 140px)" },
            display: "flex",
            flexDirection: "column",
            order: { xs: 2, md: 1 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 1.75,
              py: 1.35,
              borderBottom: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.7),
            }}
          >
            <ReceiptLongOutlinedIcon color="primary" fontSize="small" />
            <Typography sx={{ fontWeight: 800, flex: 1 }}>
              {t("Tpv.ticketList")}
            </Typography>
            <Chip
              size="small"
              color="primary"
              label={tickets.length}
              sx={{ fontWeight: 800, height: 22 }}
            />
          </Stack>
          <Box
            sx={{
              overflow: "auto",
              flex: 1,
              maxHeight: { xs: 280, md: 420, lg: "none" },
            }}
          >
            {tickets.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 1.75, py: 2 }}
              >
                {t("Tpv.pickOrCreateTicket")}
              </Typography>
            ) : (
              tickets.map((row, idx) => {
                const active = Number(row.id) === Number(selectedTicketId);
                const pay = ticketPaymentKind(row);
                const payLabel =
                  pay === "mix"
                    ? t("Tpv.payMix")
                    : pay === "card"
                      ? t("Tpv.card")
                      : pay === "cash"
                        ? t("Tpv.cash")
                        : null;
                return (
                  <Box
                    key={row.id}
                    onClick={() => goTicket(row.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1.1,
                      cursor: "pointer",
                      borderBottom:
                        idx < tickets.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.55)}`
                          : "none",
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "transparent",
                      borderLeft: "3px solid",
                      borderLeftColor: active
                        ? theme.palette.primary.main
                        : row.cerrado
                          ? "transparent"
                          : theme.palette.success.main,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.07),
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "0.95rem" }}
                        noWrap
                      >
                        #{row.nTicket}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        {formatTpvDate(row.openedAt)} · {formatEur(row.total)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={row.cerrado ? t("Tpv.closed") : t("Tpv.open")}
                      sx={{
                        ...statusChipSx(theme, !!row.cerrado),
                        height: 22,
                        flexShrink: 0,
                      }}
                    />
                    {payLabel ? (
                      <Chip
                        size="small"
                        label={payLabel}
                        sx={{
                          ...paymentChipSx(theme, pay),
                          height: 22,
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <IconButton
                      size="small"
                      aria-label={t("Tpv.printTicket")}
                      onClick={(e) => {
                        e.stopPropagation();
                        printTicket(row.id);
                      }}
                      sx={{ color: "primary.main", flexShrink: 0 }}
                    >
                      <PrintOutlinedIcon fontSize="small" />
                    </IconButton>
                    {!caja?.cerrado ? (
                      <IconButton
                        size="small"
                        aria-label={t("Tpv.deleteTicket")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTicketTarget(row);
                        }}
                        disabled={busy}
                        sx={{ color: "error.main", flexShrink: 0 }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>

        {/* Ticket / POS — centro */}
        <Box sx={{ minWidth: 0, order: { xs: 1, md: 2 } }}>
          {ticket ? (
            <Paper sx={pageContentCardSx(theme)} elevation={0}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{ mb: 2 }}
                alignItems={{ sm: "center" }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    minWidth: 120,
                  }}
                >
                  <Typography sx={{ ...pageSectionTitleSx, mb: 0.25 }}>
                    {t("Tpv.nTicket")}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
                    {ticket.nTicket || "—"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ ...pageSectionTitleSx, mb: 0.25 }}>
                    {t("Tpv.client")}
                  </Typography>
                  <Typography noWrap sx={{ fontWeight: 600 }}>
                    {ticket.clienteNombre || ""}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ ...pageSectionTitleSx, mb: 0.25 }}>
                    {t("Tpv.warehouse")}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {t("Tpv.ventaPrivada")}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={ticket.cerrado ? t("Tpv.closed") : t("Tpv.open")}
                  sx={{
                    ...statusChipSx(theme, !!ticket.cerrado),
                    alignSelf: { xs: "flex-start", sm: "center" },
                  }}
                />
                {ticket.cerrado && isAdmin && !caja?.cerrado ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setReopenTicketOpen(true)}
                    disabled={busy}
                    sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                  >
                    {t("Tpv.reopenTicket")}
                  </Button>
                ) : null}
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                sx={{ mb: 1.5 }}
                alignItems={{ md: "stretch" }}
              >
                <TextField
                  inputRef={eanRef}
                  label={t("Tpv.ean")}
                  value={ean}
                  onChange={(e) => setEan(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addByEan();
                    }
                  }}
                  disabled={locked}
                  size="small"
                  sx={{ ...pageFieldSx(theme), flex: 1, alignSelf: "center" }}
                />
                <Box
                  sx={{
                    ...pagePrimaryButtonSx(theme),
                    minWidth: 148,
                    height: 48,
                    px: 2,
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      opacity: 0.9,
                      lineHeight: 1,
                    }}
                  >
                    {t("Tpv.total")}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: "1.15rem",
                      lineHeight: 1.15,
                    }}
                  >
                    {formatEur(total)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<PointOfSaleOutlinedIcon />}
                  onClick={openCobro}
                  disabled={!ticket || busy || locked}
                  sx={{
                    ...pagePrimaryButtonSx(theme),
                    minWidth: 148,
                    height: 48,
                    py: 0,
                  }}
                >
                  {t("Tpv.charge")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PrintOutlinedIcon />}
                  onClick={() => printTicket(ticket.id)}
                  disabled={busy || !(ticket.lineas || []).length}
                  sx={{
                    ...pagePrimaryButtonSx(theme),
                    minWidth: 148,
                    height: 48,
                    py: 0,
                    bgcolor: theme.palette.info.main,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.info.main, 0.35)}`,
                    "&:hover": { bgcolor: theme.palette.info.dark },
                  }}
                >
                  {t("Tpv.printTicket")}
                </Button>
              </Stack>

              <Box sx={{ position: "relative", mb: 2 }}>
                <TextField
                  inputRef={buscaRef}
                  label={t("Tpv.searchProduct")}
                  helperText={locked ? undefined : t("Tpv.freeTextHint")}
                  value={busca}
                  onChange={(e) => onSearch(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  disabled={locked}
                  size="small"
                  fullWidth
                  sx={pageFieldSx(theme)}
                />
                {!locked && String(busca).trim().length >= 1 ? (
                  <Paper
                    elevation={8}
                    sx={{
                      position: "absolute",
                      zIndex: 8,
                      left: 0,
                      right: 0,
                      maxHeight: 320,
                      overflow: "auto",
                      mt: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    {hits.map((h) => (
                      <Box
                        key={h.id}
                        sx={{
                          px: 1.5,
                          py: 1,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() => addByEan(h.ean)}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {h.concepto}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {h.ean} · {formatEur(h.pvp)}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        cursor: "pointer",
                        borderTop: hits.length
                          ? `1px solid ${alpha(theme.palette.divider, 0.7)}`
                          : "none",
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.warning.main, 0.16),
                        },
                      }}
                      onClick={() => addFreeText(busca)}
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {t("Tpv.addFreeText", { text: String(busca).trim() })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Tpv.freeTextHint")}
                      </Typography>
                    </Box>
                  </Paper>
                ) : null}
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.7),
                  overflow: "auto",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.background.default, 0.7),
                        "& th": {
                          fontWeight: 800,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.35,
                          color: "text.secondary",
                        },
                      }}
                    >
                      <TableCell>{t("Tpv.code")}</TableCell>
                      <TableCell>{t("Tpv.concept")}</TableCell>
                      <TableCell align="right">{t("Tpv.qty")}</TableCell>
                      <TableCell align="right">{t("Tpv.pvp")}</TableCell>
                      <TableCell align="right">{t("Tpv.dto")}</TableCell>
                      <TableCell align="right">{t("Tpv.amount")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(ticket.lineas || []).map((line) => (
                      <TableRow key={line.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {line.ean}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <TextField
                            defaultValue={line.concepto}
                            key={`${line.id}-c-${line.concepto}`}
                            size="small"
                            disabled={locked}
                            fullWidth
                            onBlur={(e) =>
                              updateLine(line, { concepto: e.target.value })
                            }
                            onKeyDown={onLineFieldKeyDown}
                            sx={pageFieldSx(theme)}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>
                          <TextField
                            defaultValue={line.cantidad}
                            key={`${line.id}-q-${line.cantidad}`}
                            size="small"
                            disabled={locked}
                            onBlur={(e) =>
                              updateLine(line, { cantidad: e.target.value })
                            }
                            onKeyDown={onLineFieldKeyDown}
                            sx={pageFieldSx(theme)}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ width: 120, minWidth: 120 }}
                        >
                          <TextField
                            defaultValue={line.pvp}
                            key={`${line.id}-p-${line.pvp}`}
                            size="small"
                            disabled={locked}
                            onBlur={(e) =>
                              updateLine(line, { pvp: e.target.value })
                            }
                            onKeyDown={onLineFieldKeyDown}
                            sx={{
                              ...pageFieldSx(theme),
                              width: 100,
                              "& .MuiInputBase-input": { textAlign: "right" },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ width: 120, minWidth: 120 }}
                        >
                          <TextField
                            defaultValue={line.dto}
                            key={`${line.id}-d-${line.dto}`}
                            size="small"
                            disabled={locked}
                            onBlur={(e) =>
                              updateLine(line, { dto: e.target.value })
                            }
                            onKeyDown={onLineFieldKeyDown}
                            sx={{
                              ...pageFieldSx(theme),
                              width: 100,
                              "& .MuiInputBase-input": { textAlign: "right" },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatEur(line.importe)}
                        </TableCell>
                        <TableCell>
                          {!locked ? (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeLine(line)}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                ...pageContentCardSx(theme),
                minHeight: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1.5,
              }}
              elevation={0}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                }}
              >
                <ReceiptLongOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography color="text.secondary" sx={{ maxWidth: 360 }}>
                {caja?.cerrado
                  ? t("Tpv.cajaClosedHint")
                  : t("Tpv.pickOrCreateTicket")}
              </Typography>
              {!caja?.cerrado ? (
                <Button
                  variant="contained"
                  startIcon={<AddOutlinedIcon />}
                  onClick={handleNewTicket}
                  sx={pagePrimaryButtonSx(theme)}
                >
                  {t("Tpv.newTicket")}
                </Button>
              ) : null}
            </Paper>
          )}
        </Box>

        {/* Resumen caja — derecha */}
        <Paper
          elevation={0}
          sx={{
            ...pageContentCardSx(theme),
            position: { lg: "sticky" },
            top: { lg: 12 },
            order: { xs: 3, md: 3 },
            gridColumn: { md: "1 / -1", lg: "auto" },
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 1.25 }}>
            {t("Tpv.resume", { id: caja?.id })}
          </Typography>
          <Stack spacing={0.75}>
            <KpiRow
              label={t("Tpv.apertura")}
              value={formatEur(caja?.importeIni)}
            />
            <KpiRow
              label={t("Tpv.ingCash")}
              value={formatEur(caja?.ingMetalico)}
              accent={theme.palette.success.main}
            />
            <KpiRow
              label={t("Tpv.ingCard")}
              value={formatEur(caja?.ingTarjeta)}
              accent={theme.palette.info.main}
            />
            <KpiRow
              label={t("Tpv.totalCaja")}
              value={formatEur(totalCaja)}
              accent={theme.palette.primary.main}
            />
            <TextField
              label={t("Tpv.aEntregar")}
              value={aEntregar}
              onChange={(e) => setAEntregar(e.target.value)}
              disabled={!!caja?.cerrado}
              size="small"
              fullWidth
              sx={{ ...pageFieldSx(theme), mt: 0.5 }}
            />
            <KpiRow
              label={t("Tpv.enCaja")}
              value={formatEur(caja?.cerrado ? caja.importeFin : enCaja)}
            />
            <KpiRow
              label={t("Tpv.openTickets")}
              value={caja?.openTickets ?? 0}
              accent={
                Number(caja?.openTickets) > 0
                  ? theme.palette.warning.main
                  : undefined
              }
            />
            {!caja?.cerrado ? (
              <Button
                color="error"
                variant="contained"
                onClick={handleCloseCaja}
                disabled={busy}
                sx={{
                  mt: 1,
                  fontWeight: 800,
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                {t("Tpv.closeCaja")}
              </Button>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Button
                  startIcon={<PrintOutlinedIcon />}
                  onClick={printCaja}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  {t("Tpv.printClose")}
                </Button>
                {isAdmin ? (
                  <Button
                    color="warning"
                    onClick={() => setReopenCajaOpen(true)}
                    sx={{ fontWeight: 700, textTransform: "none" }}
                  >
                    {t("Tpv.reopenCaja")}
                  </Button>
                ) : null}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={cobroOpen}
        onClose={() => setCobroOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("Tpv.charge")} #{ticket?.nTicket}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              label={t("Tpv.total")}
              value={formatEur(total)}
              disabled
              sx={pageFieldSx(theme)}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant={efectivoOn ? "contained" : "outlined"}
                color={efectivoOn ? "success" : "inherit"}
                onClick={() => {
                  const next = !efectivoOn;
                  setEfectivoOn(next);
                  setEntregaE(next ? String(total) : "0");
                }}
              >
                {t("Tpv.cash")}
              </Button>
              <Button
                variant={tarjetaOn ? "contained" : "outlined"}
                color={tarjetaOn ? "success" : "inherit"}
                onClick={() => {
                  const next = !tarjetaOn;
                  setTarjetaOn(next);
                  setEntregaC(next ? String(total) : "0");
                }}
              >
                {t("Tpv.card")}
              </Button>
            </Stack>
            <TextField
              label={t("Tpv.entregaCash")}
              value={entregaE}
              onChange={(e) => setEntregaE(e.target.value)}
              disabled={!efectivoOn || ticket?.cerrado}
              sx={pageFieldSx(theme)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
            <TextField
              label={t("Tpv.entregaCard")}
              value={entregaC}
              onChange={(e) => setEntregaC(e.target.value)}
              disabled={!tarjetaOn || ticket?.cerrado}
              sx={pageFieldSx(theme)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
            <TextField
              label={t("Tpv.change")}
              value={formatEur(cambio)}
              disabled
              sx={pageFieldSx(theme)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCobroOpen(false)}>{t("Tpv.cancel")}</Button>
          <Button
            startIcon={<PrintOutlinedIcon />}
            onClick={() => printTicket()}
          >
            {t("Tpv.printTicket")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCobro}
            disabled={busy || ticket?.cerrado}
          >
            {t("Tpv.charged")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reopenTicketOpen}
        onClose={() => !busy && setReopenTicketOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.reopenTicket")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t("Tpv.reopenTicketConfirm", { n: ticket?.nTicket })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenTicketOpen(false)} disabled={busy}>
            {t("Tpv.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={confirmReopenTicket}
            disabled={busy}
          >
            {t("Tpv.reopenTicket")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTicketTarget}
        onClose={() => !busy && setDeleteTicketTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.deleteTicket")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t("Tpv.deleteTicketConfirm", {
              n: deleteTicketTarget?.nTicket,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTicketTarget(null)} disabled={busy}>
            {t("Tpv.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteTicket}
            disabled={busy}
          >
            {t("Tpv.deleteTicket")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeCajaOpen}
        onClose={() => !busy && setCloseCajaOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.closeCaja")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("Tpv.closeCajaHint")}
            </Typography>
            <KpiRow
              label={t("Tpv.ingCash")}
              value={formatEur(caja?.ingMetalico)}
            />
            <KpiRow
              label={t("Tpv.ingCard")}
              value={formatEur(caja?.ingTarjeta)}
            />
            <KpiRow label={t("Tpv.totalCaja")} value={formatEur(totalCaja)} />
            <TextField
              label={t("Tpv.aEntregar")}
              value={aEntregar}
              onChange={(e) => setAEntregar(e.target.value)}
              autoFocus
              fullWidth
              sx={pageFieldSx(theme)}
              helperText={
                Number(String(aEntregar).replace(",", ".") || 0) === 0
                  ? t("Tpv.emptyHandoverHint")
                  : undefined
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
            <KpiRow label={t("Tpv.enCaja")} value={formatEur(enCaja)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseCajaOpen(false)} disabled={busy}>
            {t("Tpv.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmCloseCaja}
            disabled={busy}
          >
            {t("Tpv.closeCaja")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reopenCajaOpen}
        onClose={() => !busy && setReopenCajaOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.reopenCaja")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>{t("Tpv.reopenCajaConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenCajaOpen(false)} disabled={busy}>
            {t("Tpv.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmReopenCaja}
            disabled={busy}
          >
            {t("Tpv.reopenCaja")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
